import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { _getCookieValue, _getConfig, getUserData, getBaseUrl } from "../Helper/commonFunctions_v2";

const ChatContext = createContext();

export { ChatContext }; // Export the context object

export const useChat = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error("useChat must be used within a ChatProvider");
    }
    return context;
};

export const ChatProvider = ({ children, location }) => {
    const [messages, setMessages] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const [ws, setWs] = useState(null);
    const [connectionAttempts, setConnectionAttempts] = useState(0);
    const [shouldReconnect, setShouldReconnect] = useState(false);
    const [currentTechpackId, setCurrentTechpackId] = useState(null);
    const reconnectTimeoutRef = useRef(null);

    // console.log("ChatProvider location", location);

    // Function to set the current techpack ID
    const setTechpackId = (techpackId) => {
        // console.log("Setting techpack ID:", techpackId);
        setCurrentTechpackId(techpackId);
    };

    // STOMP Frame Builders
    const buildConnectFrame = (token, topicId, userId) => {
        // Use token as is - it should already have "Bearer " prefix if needed
        token = "Bearer " + token;
        return `CONNECT\naccept-version:1.2\nAuthorization:${token}\n\n\x00`;
    };

    const buildSubscribeFrame = (userId, topicId) => {
        return `SUBSCRIBE\nid:sub-0\ndestination:/topic/chat/${topicId}/${userId}\n\n\x00`;
    };

    const buildSendFrame = (message, topicId, userId) => {
        const body = JSON.stringify({
            topicType: topicId,
            topicId: userId,
            userId: userId,
            username: getUserData()?.firstName + " " + getUserData()?.lastName || "Anonymous",
            name: getUserData()?.firstName + " " + getUserData()?.lastName || "Anonymous",
            content: message,
        });
        return `SEND\ndestination:/app/chat\ncontent-type:application/json\n\n${body}\x00`;
    };

    // Reconnection logic
    const scheduleReconnect = () => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }

        const maxAttempts = 10;
        const baseDelay = 1000; // 1 second

        if (connectionAttempts < maxAttempts) {
            const delay = baseDelay * Math.pow(2, connectionAttempts); // Exponential backoff
            // console.log(`Scheduling reconnection attempt ${connectionAttempts + 1} in ${delay}ms`);

            reconnectTimeoutRef.current = setTimeout(() => {
                setConnectionAttempts((prev) => prev + 1);
                // Force reconnection by setting the flag
                setShouldReconnect(true);
            }, delay);
        } else {
            // console.log("Max reconnection attempts reached");
        }
    };

    // Initialize WebSocket connection
    useEffect(() => {
        // console.log("ChatContext: Initializing WebSocket connection - Component mounted", { ws: ws?.readyState, shouldReconnect });

        // Create a new connection if ws is null or if we need to reconnect
        if (ws === null || shouldReconnect) {
            const config = _getConfig();
            let token = config?.headers?.Authorization || localStorage.getItem("token") || sessionStorage.getItem("token") || _getCookieValue("token");

            const topicId = location?.pathname?.includes("techpack") ? "techpack" : "geniemode"; // You can make this dynamic
            const userId = getUserData()?.id;

            if (!token) {
                // console.log("No token available, cannot connect to chat");
                return;
            }

            // console.log("Creating new WebSocket connection...");
            let wsUrl = `${getBaseUrl("notes").replace("https://", "wss://")}/ws/websocket`;
            const newWs = new WebSocket(wsUrl);

            newWs.onopen = () => {
                // console.log("WebSocket opened. Sending CONNECT frame...");
                setIsConnected(true);
                setConnectionAttempts(0); // Reset connection attempts on successful connection

                try {
                    // Send CONNECT frame with headers
                    const connectFrame = buildConnectFrame(token, topicId, userId);
                    // console.log("Sending CONNECT frame:", connectFrame);
                    newWs.send(connectFrame);

                    // Wait a bit then send SUBSCRIBE frame
                    setTimeout(() => {
                        try {
                            const subscribeFrame = buildSubscribeFrame(userId, topicId);
                            // console.log("Sending SUBSCRIBE frame:", subscribeFrame);
                            newWs.send(subscribeFrame);
                            // console.log("Subscribed to chat topic");
                        } catch (error) {
                            console.error("Error sending SUBSCRIBE frame:", error);
                        }
                    }, 1000);
                } catch (error) {
                    console.error("Error sending CONNECT frame:", error);
                }
            };

            newWs.onmessage = (event) => {
                const message = event.data;
                // console.log("[Received]:", message);

                // Parse STOMP message
                if (message.includes("MESSAGE")) {
                    try {
                        // Extract JSON body from STOMP frame
                        const bodyStart = message.indexOf("\n\n") + 2;
                        const bodyEnd = message.lastIndexOf("\x00");
                        const jsonBody = message.substring(bodyStart, bodyEnd);
                        const parsedMessage = JSON.parse(jsonBody);
                        let content = parsedMessage.content;
                        if (typeof content === "string") {
                            try {
                                content = JSON.parse(content);
                            } catch (error) {
                                console.error("Error parsing message content:", error);
                            }
                        }

                        // Check if it's a file message
                        if (content?.fileData) {
                            // Handle file message
                            setMessages((prev) => {
                                // Remove any existing "LOADING" messages when a new message arrives
                                const filteredMessages = prev.filter((msg) => msg.content !== "LOADING");
                                // const filteredMessages = prev.filter((msg) => msg.content !== "askjdad");
                                return [
                                    ...filteredMessages,
                                    {
                                        id: Date.now(),
                                        type: "file",
                                        fileName: content.fileData.fileName,
                                        fileSize: content.fileData.fileSize,
                                        fileType: content.fileData.fileType,
                                        fileIcon: content.fileData.fileIcon,
                                        fileUrl: content.fileData.fileUrl,
                                        bucket: content.fileData.bucket,
                                        key: content.fileData.key,
                                        userId: content.userId,
                                        name: parsedMessage.name || "Anonymous",
                                        username: parsedMessage.username || "Anonymous",
                                        timestamp: new Date().toISOString(),
                                    },
                                ];
                            });
                        } else {
                            // Handle regular message
                            setMessages((prev) => {
                                // Remove any existing "LOADING" messages when a new message arrives
                                const filteredMessages = prev.filter((msg) => msg.content !== "LOADING");
                                // const filteredMessages = prev.filter((msg) => msg.content !== "askjdad");
                                return [
                                    ...filteredMessages,
                                    {
                                        id: Date.now(),
                                        content: parsedMessage.content,
                                        userId: parsedMessage.userId,
                                        username: parsedMessage.username || "Anonymous",
                                        name: parsedMessage.name || "Anonymous",
                                        timestamp: new Date().toISOString(),
                                        type: "user",
                                    },
                                ];
                            });
                        }
                    } catch (error) {
                        console.error("Error parsing message:", error);
                    }
                }
            };

            newWs.onclose = (event) => {
                // console.log("WebSocket closed", event.code, event.reason);
                // console.log("Close event details:", {
                //     code: event.code,
                //     reason: event.reason,
                //     wasClean: event.wasClean,
                //     type: event.type,
                // });
                setIsConnected(false);

                // Don't reconnect if it was a clean close or if we're unmounting
                if (!event.wasClean && event.code !== 1000) {
                    scheduleReconnect();
                }
            };

            newWs.onerror = (error) => {
                console.error("WebSocket error:", error);
                console.error("Error details:", {
                    message: error.message,
                    type: error.type,
                    target: error.target,
                });
                setIsConnected(false);
            };

            setWs(newWs);
            setShouldReconnect(false); // Reset the reconnect flag
        }

        return () => {
            // Cleanup will be handled by the context unmount
        };
    }, [ws, shouldReconnect]); // Now depends on ws state and shouldReconnect flag

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (ws) {
                // console.log("ChatContext: Cleaning up WebSocket connection - Component unmounting");
                ws.close();
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
        };
    }, [ws]);

    const sendMessage = (message, typeOfChat) => {
        if (message.trim() && ws && isConnected) {
            const topicId = typeOfChat === "techpack" ? "techpack" : "geniemode"; // You can make this dynamic
            const userId = getUserData()?.id;

            // Send STOMP SEND frame
            ws.send(buildSendFrame(message.trim(), topicId, userId));
            return true;
        }
        return false;
    };

    const sendFileMessage = (fileMessageData, typeOfChat) => {
        if (ws && isConnected) {
            const topicId = typeOfChat === "techpack" ? "techpack" : "geniemode"; // You can make this dynamic
            const userId = getUserData()?.id;

            // Send STOMP SEND frame with file URL
            const fileFrame = buildSendFrame(JSON.stringify(fileMessageData), topicId, userId);
            ws.send(fileFrame);
            return true;
        }
        return false;
    };

    // Function to switch topics and clear messages
    const switchTopic = (newTopicId) => {
        if (ws && isConnected) {
            const userId = getUserData()?.id;

            // Clear current messages
            setMessages([]);

            // Send new subscription frame
            try {
                const subscribeFrame = buildSubscribeFrame(userId, newTopicId);
                // console.log("Switching to new topic:", newTopicId);
                // console.log("Sending SUBSCRIBE frame:", subscribeFrame);
                ws.send(subscribeFrame);
                // console.log("Subscribed to new chat topic:", newTopicId);
            } catch (error) {
                console.error("Error switching topic:", error);
            }
        }
    };

    // Manual reconnect function
    const reconnect = () => {
        // console.log("Manual reconnect triggered");

        // Clear any existing reconnection timeout
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }

        // Reset connection attempts
        setConnectionAttempts(0);

        // Close existing connection if any
        if (ws) {
            // console.log("Closing existing WebSocket connection");
            ws.close();
        }

        // Set isConnected to false immediately
        setIsConnected(false);

        // Trigger reconnection
        // console.log("Setting shouldReconnect to true");
        setShouldReconnect(true);
    };

    const value = {
        messages,
        setMessages,
        isConnected,
        sendMessage,
        sendFileMessage,
        reconnect,
        switchTopic,
        setTechpackId,
        currentTechpackId,
    };

    return (
        <ChatContext.Provider location={location} value={value}>
            {children}
        </ChatContext.Provider>
    );
};
